import { Button, Form, Input, notification, Spin } from 'antd';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  checkEmployeesOvertimeFourHoursInTwoDayType,
  checkEmployeesOvertimeType,
  dataForOvertimeUsersTable,
} from 'shared/helpers/utils';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import CommonTable from 'features/tables/commonTable';
import { TCommonColumns, TOvertimeGetByIdResponse, TOvertimeGetResponse } from 'shared/types/overtime.types';
import { TUserOvertimeInfo, TUserOvertimeInfoPaginated } from 'shared/types/overtime.types';
import { DeleteOutlined, DownloadOutlined, EditOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import ModalCancelSubmit from 'shared/components/modalCancelSubmit/ModalCancelSubmit';
import { useNavigate, useParams } from 'react-router-dom';
import { OvertimeStatuses, OvertimeStatusesBack } from 'shared/enums/overtime';
import {
  useCheckOvertimeUserMutation,
  useCreateOvertimeUserMutation,
  useDeleteOvertimeMutation,
  useDeleteOvertimeUserMutation,
  useLazyGetOvertimeByIdQuery,
  useLazyGetOvertimeUsersQuery,
  useUpdateOvertimeMutation,
  useUpdateOvertimeUserMutation,
} from 'app/services/edpApiV1/overtime';
import { PICKER_DATE_MASK } from 'shared/constants';
import moment from 'moment';
import { checkOvertimeCompensationType, checkOvertimeCompensationTypeToBack } from 'shared/helpers/type-check';
import EmployeeInput from 'features/absence/madalAddOneAbsence/employeeInput';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { TDivisionEmployees } from 'shared/types/types';
import { useLazyGetEmployeesQuery } from 'app/services/edpApiV1/employees';
import { downloadExcellOvertime } from 'shared/helpers/excell/excell';
import { useDebounce } from 'shared/hooks';
import Search from 'antd/es/transfer/search';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppDispatch, useAppSelector } from 'app/store';
import { viewerModel } from 'entities/viewer';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { uiModel } from 'entities/ui';
import { RvdTabs } from 'features/rvd';
import OvertimeSidebar from '../createOvertimeRequest/sidebar/OvertimeSidebar';
import BadgeData from '../overtimeArchive/bageData/BadgeData';
import style from './index.module.scss';

const FORM_ITEM_NAME = 'signatoryUpdate';
const FORM_USER_COMMENT = 'comment';

const UpdateOvertime = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isSidebarCreateOpen, setIsSidebarCreateOpen] = useState<boolean>(false);
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<TUserOvertimeInfo | undefined>();
  const [currentOvertime, setCurrentOvertime] = useState<TOvertimeGetByIdResponse>();
  const [currentOvertimeUsers, setCurrentOvertimeUsers] = useState<TUserOvertimeInfoPaginated>();
  const [getEmployees, { data: employees }] = useLazyGetEmployeesQuery();
  const [search, setSearch] = useState<string | undefined>();
  const debouncedSearch = useDebounce(search);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const currentOrganizationid = useAppSelector(viewerModel.me.selectors.getMeOrganizationId);
  const profile = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const [signatory, setSignatory] = useState<TDivisionEmployees | null>(null);
  const [getSignatories, { data: signatories }] = useLazyGetDivisionEmployeesQuery();

  const [isLoadAllowed, setIsLoadAllowed] = useState<boolean>(false);
  const [saveOvertimeLoading, setSaveOvertimeLoading] = useState<boolean>(false);

  const [getOvertimeById, { data: overtimeResponse, isLoading, isFetching }] = useLazyGetOvertimeByIdQuery();
  const [getOvertimes, { data: overtimeUserResponse, isFetching: isUserFetching, isLoading: isUserLoading }] =
    useLazyGetOvertimeUsersQuery();

  useEffect(() => {
    getOvertimeById({ id: id ? +id : 0 });
  }, []);

  useEffect(() => {
    getOvertimes({
      size: currentSize,
      page: currentPage,
      overtimeId: id ? +id : 0,
    });
  }, [currentSize, currentPage, id, getOvertimes]);

  useEffect(() => {
    getOvertimes({
      size: currentSize,
      page: currentPage,
      overtimeId: id ? +id : 0,
      personnelNumber: debouncedSearch,
    });
  }, [debouncedSearch]);

  const onClear = () => {
    getOvertimes({
      size: currentSize,
      page: currentPage,
      overtimeId: id ? +id : 0,
    });
  };

  const navigate = useNavigate();

  const usersToMutate = useRef<{ update: TUserOvertimeInfo[]; delete: number[]; create: TUserOvertimeInfo[] }>({
    update: [],
    delete: [],
    create: [],
  });

  const [deleteUserQuery] = useDeleteOvertimeUserMutation();
  const [updateUserQuery] = useUpdateOvertimeUserMutation();
  const [createUserQuery] = useCreateOvertimeUserMutation();
  const [deleteOvertimeQuery, { isLoading: isDeletingLoading }] = useDeleteOvertimeMutation();
  const [updateOvertimeQuery] = useUpdateOvertimeMutation();
  const [checkErrors, { isLoading: isCheckLoading }] = useCheckOvertimeUserMutation();

  const dispatch = useAppDispatch();

  const breadCrumbs = [
    { name: 'Сверхурочные', link: '/overtime' },
    { name: 'Редактировать СУ', link: `/overtime/update/${id}` },
  ];

  useEffect(() => {
    if (employees?.data?.length) {
      const [user] = employees.data;
      form.setFieldValue(FORM_ITEM_NAME, user.id);
      setSignatory({ ...user, division: user.division?.name || '', position: '' });
      getSignatories({
        page: 1,
        size: 10,
        search: `${user.lastName} ${user.name} ${user.middleName}`,
        divisionId: user.division?.divisionId,
        allAvailableOrganizations,
      });
    }
  }, [employees]);

  useEffect(() => {
    if (overtimeResponse) {
      form.setFieldValue(FORM_USER_COMMENT, overtimeResponse?.userComment);

      getEmployees({
        isActive: true,
        page: 1,
        size: 1,
        personnelNumber: overtimeResponse.docSignatory,
        allAvailableOrganizations,
        organizationId: currentOrganizationid,
      });
    }
  }, [overtimeResponse, allAvailableOrganizations]);

  useEffect(() => {
    if (overtimeResponse) {
      setCurrentOvertime(overtimeResponse);
    }
  }, [overtimeResponse]);

  useEffect(() => {
    if (overtimeUserResponse) {
      setCurrentOvertimeUsers(overtimeUserResponse);
    }
  }, [overtimeUserResponse]);

  useEffect(() => {
    if (currentOvertime?.employees) {
      let isUserWrong = false;
      for (const user of currentOvertime?.employees) {
        if (user.comment) {
          setIsLoadAllowed(false);
          isUserWrong = true;
        }
      }
      if (!isUserWrong) {
        setIsLoadAllowed(true);
      }
    }
  }, [currentOvertime?.employees]);

  const download = () => {
    if (currentOvertime && currentOvertimeUsers?.data) {
      downloadExcellOvertime({ ...currentOvertime, employees: currentOvertimeUsers.data });
    }
  };

  const onEmployeeDelete = (id: number) => {
    const justCreated = usersToMutate.current.create.find(elem => elem.id === id);
    if (justCreated) {
      usersToMutate.current.create = usersToMutate.current.create.filter(elem => elem.id !== id);
    } else {
      usersToMutate.current.delete.push(id);
    }
    const newCurrentUsers: TUserOvertimeInfo[] = [...currentOvertimeUsers!.data].filter(user => user.id !== id);
    setCurrentOvertimeUsers({ ...currentOvertimeUsers!, data: newCurrentUsers });
  };

  const rowClicked = (id: number) => {
    if (
      overtimeResponse?.status === OvertimeStatusesBack.APPROVED ||
      overtimeResponse?.status === OvertimeStatusesBack.CANCELED
    ) {
      return;
    }
    if (currentOvertime?.hr?.id !== profile?.id) {
      return;
    }
    const user = currentOvertimeUsers?.data.find(item => item.id === id);

    if (user) {
      const fixedUser = {
        ...user,
        toDate: moment(user.toDate).format(PICKER_DATE_MASK),
        type: checkOvertimeCompensationType(user.type),
      };
      setCurrentUser(fixedUser);
    }
    setIsSidebarOpen(prev => !prev);
  };

  const onClose = () => {
    setIsSidebarOpen(false);
  };

  const onCreateClose = () => {
    setIsSidebarCreateOpen(false);
  };

  const onFinish = (overtimeUser: TUserOvertimeInfo) => {
    const justCreated = usersToMutate.current.create.find(elem => elem.id === overtimeUser.id);
    if (justCreated) {
      usersToMutate.current.create = usersToMutate.current.create.map(elem => {
        if (elem.id === overtimeUser.id) {
          return overtimeUser;
        }
        return elem;
      });
    } else {
      usersToMutate.current.update.push(overtimeUser);
    }
    const newOvertimeUsers = currentOvertimeUsers?.data.map(user => {
      if (user.id === overtimeUser.id) {
        return overtimeUser;
      }
      return user;
    });
    setIsSidebarOpen(false);
    setCurrentOvertime({ ...currentOvertime! });
    setCurrentOvertimeUsers({ ...currentOvertimeUsers!, data: newOvertimeUsers ? newOvertimeUsers : [] });
  };

  const onCreateFinish = (overtimeUser: TUserOvertimeInfo) => {
    const overtime = { ...currentOvertime! };
    const overtimeUserId = Math.random();
    overtimeUser.id = overtimeUserId;
    const overtimeUsers = [...(currentOvertimeUsers?.data ? currentOvertimeUsers?.data : []), overtimeUser];
    usersToMutate.current.create.push(overtimeUser);
    setCurrentOvertime(overtime);
    setCurrentOvertimeUsers({ ...currentOvertimeUsers!, data: overtimeUsers ? overtimeUsers : [] });
    setIsSidebarCreateOpen(false);
  };

  const columns: TCommonColumns = [
    {
      title: 'Дата работ',
      dataIndex: 'toDate',
      key: 'toDate',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Табельный номер',
      dataIndex: 'personnelNumber',
      key: 'personnelNumber',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Время работы',
      dataIndex: 'workDuration',
      key: 'workDuration',
    },
    {
      title: 'Способ компенсации',
      dataIndex: 'type',
      key: 'type',
    },
  ];

  if (
    !(
      overtimeResponse?.status === OvertimeStatusesBack.APPROVED ||
      overtimeResponse?.status === OvertimeStatusesBack.CANCELED
    ) &&
    currentOvertime?.hr?.id === profile?.id
  ) {
    columns.push({
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <span
          className={`${style['table-delete']} ${
            currentOvertime?.status === OvertimeStatuses.LOADED_CPRODUCT ? style.disabled : ''
          }`}
          onClick={e => {
            e.stopPropagation();
            if (currentOvertime?.status !== OvertimeStatuses.LOADED_CPRODUCT) {
              onEmployeeDelete(record.id!);
            }
          }}
        >
          Удалить
        </span>
      ),
    });
  }

  const onSubmitCancelModal = () => {
    setOpenCanelModal(false);
    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
    navigate('/overtime');
  };

  const onCancelCancelModal = () => {
    setOpenCanelModal(false);
  };

  const cancelModalOpen = () => {
    if (currentOvertime?.status !== OvertimeStatuses.LOADED_CPRODUCT) {
      setOpenCanelModal(true);
    } else {
      dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
      navigate('/overtime');
    }
  };

  const deleteOvertime = async () => {
    await deleteOvertimeQuery({ id: id ? +id : 0 });
    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
    navigate('/overtime');
  };

  const openDrawerCreate = () => {
    setIsSidebarCreateOpen(true);
  };

  const saveOvertime = async (withoutSuccessNotify?: boolean) => {
    setSaveOvertimeLoading(true);
    try {
      const preparedEmployees = [...usersToMutate.current.create, ...usersToMutate.current.update].map(user => ({
        employeeId: user.userInfo.id,
        endTime: user.endTime,
        startTime: user.startTime,
        toDate: user.toDate,
        type: checkOvertimeCompensationTypeToBack(user.type),
      }));
      checkEmployeesOvertimeType(preparedEmployees);
      checkEmployeesOvertimeFourHoursInTwoDayType(preparedEmployees);

      await updateOvertimeQuery({
        id: id ? +id : 0,
        status: currentOvertime?.status || '',
        docSignatory: signatory?.personnelNumber || '',
        userComment: form.getFieldValue(FORM_USER_COMMENT),
      }).unwrap();

      for (const deleted of usersToMutate.current.delete) {
        await deleteUserQuery({ id: deleted }).unwrap();
      }

      for (const updated of usersToMutate.current.update) {
        await updateUserQuery({
          employeeId: updated.userInfo.id,
          overtimeId: currentOvertime!.id,
          endTime: updated.endTime,
          id: updated.id!,
          startTime: updated.startTime,
          toDate: updated.toDate,
          type: checkOvertimeCompensationTypeToBack(updated.type),
        }).unwrap();
      }

      const usersToCreate = usersToMutate.current.create.map(user => ({
        employeeId: user.userInfo.id,
        endTime: user.endTime,
        startTime: user.startTime,
        toDate: user.toDate,
        type: checkOvertimeCompensationTypeToBack(user.type),
      }));
      await createUserQuery({
        overtimeId: currentOvertime!.id,
        employee: usersToCreate,
      }).unwrap();
    } catch (e: any) {
      setSaveOvertimeLoading(false);
      notification.error({ message: returnErrorOrMessage(e, 'Ошибка при редактировании cверхурочных') });
      return;
    }
    if (!withoutSuccessNotify) {
      notification.success({ message: 'Заявка на сверхурочную работу изменена' });
    }
    setSaveOvertimeLoading(false);

    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
  };

  const onRefreshError = async () => {
    if (!currentOvertime?.employees) {
      return;
    }
    try {
      await saveOvertime(true);
      const users = currentOvertime?.employees.filter(elem => !!elem.comment).map(elem => Number(elem.id));
      await checkErrors({ overtimeUsersIds: users });
      notification.success({ message: 'Обновлено!' });
      await getOvertimeById({ id: id ? +id : 0 });
      await getOvertimes({ size: currentSize, page: currentPage, overtimeId: id ? +id : 0 });
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Ошибка обновления') });
    }
  };

  return (
    <>
      <div className={style['top-side']}>
        <h3 className={style.title}>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <div className={style.buttons}>
          {currentOvertime?.status !== OvertimeStatuses.LOADED_CPRODUCT ? (
            <>
              {!currentOvertime?.isComplete && currentOvertime?.hr?.id === profile?.id && (
                <Button onClick={deleteOvertime} loading={isDeletingLoading}>
                  <DeleteOutlined /> Удалить
                </Button>
              )}

              <Button type="primary" onClick={download}>
                <DownloadOutlined /> Скачать
              </Button>
              {!currentOvertime?.isComplete && currentOvertime?.hr?.id === profile?.id && (
                <Button onClick={openDrawerCreate}>
                  <EditOutlined />
                  Добавить сотрудника
                </Button>
              )}
              {!currentOvertime?.isComplete && currentOvertime?.hr?.id === profile?.id && (
                <Button
                  type="primary"
                  onClick={() => saveOvertime()}
                  loading={saveOvertimeLoading}
                  disabled={!currentOvertimeUsers?.data.length || !currentOvertime?.employees.length}
                >
                  <SaveOutlined /> Сохранить
                </Button>
              )}
            </>
          ) : null}

          <Button onClick={cancelModalOpen}>
            <StopOutlined /> Отмена
          </Button>
          <ModalCancelSubmit open={openCancelModal} onQuit={onSubmitCancelModal} onStay={onCancelCancelModal} />
        </div>
      </div>
      <OvertimeSidebar
        onClose={onClose}
        isOpen={isSidebarOpen}
        onOk={onFinish}
        currentOvertime={currentOvertime!}
        currentUser={currentUser}
      />
      <OvertimeSidebar
        onClose={onCreateClose}
        isOpen={isSidebarCreateOpen}
        onOk={onCreateFinish}
        currentOvertime={currentOvertime}
      />
      <div className={style.search}>
        <Search
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          placeholder="Введите табельный номер сотрудника"
          handleClear={onClear}
        />
      </div>
      <div className={style.statusData}>
        {overtimeResponse?.docNumber && (
          <p>
            <span>№ документа в 1С:</span>
            <span>{overtimeResponse?.docNumber}</span>
          </p>
        )}
        <p>
          <span>Статус документа:</span>
          <span>
            <BadgeData overtime={overtimeResponse as TOvertimeGetResponse} />
          </span>
        </p>
      </div>
      <Spin spinning={isUserFetching || isUserLoading || isLoading || isFetching}>
        <CommonTable
          dataSource={dataForOvertimeUsersTable(currentOvertimeUsers?.data ? currentOvertimeUsers?.data : [])}
          columns={columns}
          currentPage={currentPage}
          currentSize={currentSize}
          setCurrentPage={setCurrentPage}
          setCurrentSize={setCurrentSize}
          total={currentOvertimeUsers?.pagination.total}
          onclickToRow={rowClicked}
        />
      </Spin>
      <Form
        form={form}
        name="basic"
        layout="vertical"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={{ remember: true }}
        onFinish={() => {}}
        autoComplete="off"
      >
        <EmployeeInput
          data={signatories}
          getEmployees={getSignatories}
          setEmployee={setSignatory}
          divisionName={signatory?.division}
          label="Выберите руководителя"
          rules={[{ required: true, message: 'Выберите руководителя!' }]}
          className="form-item-overtime"
          onChangeEmployee={() => {}}
          defaultUser={currentUser?.userInfo}
          formItemName={FORM_ITEM_NAME}
          disabled={
            overtimeResponse?.status === OvertimeStatusesBack.APPROVED ||
            overtimeResponse?.status === OvertimeStatusesBack.CANCELED
          }
        />
        <Form.Item name={FORM_USER_COMMENT} label={'Введите комментарий'}>
          <Input
            placeholder="Введите комментарий"
            disabled={
              overtimeResponse?.status === OvertimeStatusesBack.APPROVED ||
              overtimeResponse?.status === OvertimeStatusesBack.CANCELED
            }
          />
        </Form.Item>
      </Form>
      {!(
        overtimeResponse?.status === OvertimeStatusesBack.APPROVED ||
        overtimeResponse?.status === OvertimeStatusesBack.CANCELED
      ) &&
        (overtimeResponse?.comment || !isLoadAllowed) &&
        currentOvertime?.hr?.id === profile?.id && (
          <div className={style.error}>
            <p className={style.nullMargin}>Ошибка: </p>
            <Button onClick={onRefreshError} loading={isCheckLoading}>
              Сохранить и проверить заявку
            </Button>
          </div>
        )}
      {overtimeResponse?.comment ? <p>{overtimeResponse?.comment}</p> : null}
      {!isLoadAllowed &&
        currentOvertime?.employees.map(user => {
          if (user.comment) {
            return <p>{user.comment}</p>;
          }
          return null;
        })}
    </>
  );
};

export default UpdateOvertime;
